import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { faroInitializer } from './faro-initializer';
import { pingAuthInterceptor } from './interceptors/ping-auth.interceptor';
import { GlobalErrorHandler } from './global-error-handler';
import { AuthService } from './services/auth.service';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import {
  provideRdsProgressSpinner,
  provideRdsTimepicker,
  provideRdsToast,
} from '@rds/angular-components';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  AbstractSecurityStorage,
  DefaultLocalStorageService,
  LogLevel,
  OpenIdConfiguration,
  provideAuth,
} from 'angular-auth-oidc-client';
import { environment } from '../environments/environment';
import { PING_CONFIG_ID } from './constants/auth.constants';

/**
 * Ping/OIDC configuration, exported so the decisions encoded here can be
 * asserted in `app.config.spec.ts`. Several of these options look like
 * tidy-up bait and each one is load-bearing — see the comments inline.
 *
 * FILM (`/film`) mirrors this config to share the same session — same
 * `configId`, same storage, same options (fulfillment-input-logic-manager
 * `src/app/app.config.ts`). Change both together: an option that differs lets
 * one app judge the shared session invalid and reset it, logging the user out
 * of both.
 */
export const pingAuthConfig: OpenIdConfiguration = {
  // Pinned, not derived. The library would default to
  // `${index}-${clientId}`; AuthService reaches into this storage entry by
  // name to clear a stale flow flag, so both sides must read one constant.
  configId: PING_CONFIG_ID,
  authority: environment.ping.authority,
  redirectUrl: environment.ping.redirectUrl,
  postLogoutRedirectUri: environment.ping.redirectUrl,
  clientId: environment.ping.clientId,
  scope: environment.ping.scope,
  responseType: 'code',
  silentRenew: true,
  useRefreshToken: true,
  autoUserInfo: true,
  // Renew 30s ahead of expiry. At the library default of 0 the periodic
  // check only fires once the token has *already* expired
  // (`shouldStartPeriodicallyCheckForConfig` returns
  // `idTokenExpired || accessTokenExpired`), which leaves no margin: a
  // single failed attempt drops the session with no second chance, and
  // every renew races the expired bearer the interceptor is still
  // attaching.
  renewTimeBeforeTokenExpiresInSeconds: 30,
  // Ping issues a 5-minute id_token against a 2-hour access token, and
  // does not mint a fresh id_token on the refresh-token grant — the
  // library carries the old one forward (`callbackHistoryAndResetJwtKeys`
  // fills `id_token` from storage when the response omits it). At the
  // default of `true`, `areAuthStorageTokensValid()` therefore reports
  // the session invalid five minutes in and stays that way forever: no
  // refresh can ever produce an unexpired id_token. A reload then landed
  // on the sign-in screen while the access token had 115 minutes left.
  //
  // The id_token is a login receipt, not a session clock. What bounds API
  // access is the access token, and identity claims are already persisted
  // in `userData`. Let the access token govern.
  triggerRefreshWhenIdTokenExpired: false,
  // Load-bearing, despite reading like a validation shortcut. Ping does not
  // reissue an id_token on the refresh grant, so the library carries the
  // stored one forward — and that one holds the *original* nonce, while
  // `refreshSessionWithRefreshTokens` has meanwhile set the local nonce to the
  // `--RefreshToken--` placeholder. `validateIdTokenNonce` compares the two,
  // fails, and resets auth data; the retry then reports the misleading
  // "no refresh token found, please login" against storage it just emptied.
  ignoreNonceAfterRefresh: true,
  // Warn is the library default. Flip to Debug to trace a silent renew — safe
  // to do because faro-initializer never forwards DEBUG, and the debug lines
  // carry the access and refresh tokens verbatim. Keep those two together.
  logLevel: LogLevel.Warn,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(
      withInterceptors([pingAuthInterceptor]),
    ),
    provideAuth({ config: pingAuthConfig }),
    // `provideAuth` hardcodes `DefaultSessionStorageService`, and sessionStorage
    // is per-tab: a new tab or a browser restart meant a full SSO round-trip
    // every time, because the refresh token died with the tab. Must come after
    // `provideAuth` — later providers win for the same token.
    //
    // The trade-off is deliberate: the refresh token now outlives the tab and
    // is readable by any XSS on this origin. Accepted for an internal portal
    // behind Ping; revisit if the app ever renders untrusted HTML.
    { provide: AbstractSecurityStorage, useClass: DefaultLocalStorageService },
    provideAnimationsAsync(),
    provideAppInitializer(faroInitializer()),
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      return auth.refresh();
    }),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    provideAppInitializer(() => inject(GoogleAnalyticsService).init()),
    provideRdsProgressSpinner(),
    provideRdsToast(),
    provideRdsTimepicker(),
  ],
};
